import { useState, useEffect, useRef } from 'react';
import { ShippingLineItem } from '../DeShippingTableWrapper';
import { LineItemUpdate } from '../utils/lineItemTransforms';
import { 
  getLineItems, 
  acceptLineItem as acceptLineItemService,
  declineLineItem as declineLineItemService,
  reacceptLineItem as reacceptLineItemService,
  StatusUpdateResult 
} from '../services/lineItemsService';

import { updateLineItemsBulk, BulkUpdateResponse } from '../services/adminControlService';

interface UseLineItemsReturn {
  lineItems: ShippingLineItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  reset: () => void;
  selectedLineItem: ShippingLineItem | null;
  setSelectedLineItem: (lineItem: ShippingLineItem | null) => void;
  updateLineItemsBulk: (orderId: string, lineItemUpdates: LineItemUpdate[]) => Promise<BulkUpdateResponse>;
  updating: boolean;
  updateError: string | null;
  acceptLineItem: (lineItem?: ShippingLineItem) => Promise<StatusUpdateResult>;
  declineLineItem: (reason?: string) => Promise<StatusUpdateResult>;
  acceptAllLineItems: () => Promise<StatusUpdateResult>;
  declineAllLineItems: (reason?: string) => Promise<StatusUpdateResult>;
  statusUpdating: boolean;
  statusUpdateError: string | null;
  reacceptLineItem: (lineItem?: ShippingLineItem) => Promise<StatusUpdateResult>;
}

export const useLineItems = (orderId: string | undefined): UseLineItemsReturn => {
  const [selectedLineItem, setSelectedLineItem] = useState<ShippingLineItem | null>(null);
  const [lineItems, setLineItems] = useState<ShippingLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);

  // Ref to store the interval ID for cleanup
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchLineItems = async () => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Clear selection state on full fetch
      setSelectedLineItem(null);
      
      const data = await getLineItems(orderId);
      setLineItems(data.data.lineItems);
    } catch (err) {
      console.error('Error fetching line items:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch line items');
    } finally {
      setLoading(false);
    }
  };

  // Silent fetch function that doesn't trigger loading state
  const fetchLineItemsSilently = async () => {
    if (!orderId) {
      return;
    }

    try {
      const data = await getLineItems(orderId);
      setLineItems(data.data.lineItems);
      
      // Update selected line item if one is currently selected
      if (selectedLineItem) {
        const updatedSelectedItem = data.data.lineItems.find(
          item => item.line_item_id === selectedLineItem.line_item_id
        );
        if (updatedSelectedItem) {
          setSelectedLineItem(updatedSelectedItem);
        }
      }
      

      
      // Clear any existing errors on successful silent fetch
      setError(null);
    } catch (err) {
      // Log error but don't update error state to avoid disrupting UI
      console.error('Error during silent line items fetch:', err);
    }
  };

  const handleUpdateLineItemsBulk = async (orderId: string, lineItemUpdates: LineItemUpdate[]): Promise<BulkUpdateResponse> => {
    try {
      setUpdating(true);
      setUpdateError(null);

      const data = await updateLineItemsBulk(orderId, lineItemUpdates);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update line items';
      setUpdateError(errorMessage);
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  const acceptLineItem = async (lineItem?: ShippingLineItem): Promise<StatusUpdateResult> => {
    const targetLineItem = lineItem || selectedLineItem;
    
    if (!targetLineItem) {
      return {
        success: false,
        message: 'No line item selected'
      };
    }

    if (targetLineItem.fulfillment_status !== 'accepted_awaiting_inspection') {
      return {
        success: false,
        message: 'Line item can only be accepted when it has been assigned and is awaiting inspection'
      };
    }

    try {
      setStatusUpdating(true);
      setStatusUpdateError(null);

      const result = await acceptLineItemService(targetLineItem.workflow_id.toString());
      
      if (!result.success) {
        setStatusUpdateError(result.message);
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept line item';
      setStatusUpdateError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setStatusUpdating(false);
    }
  };

  const reacceptLineItem = async (lineItem?: ShippingLineItem): Promise<StatusUpdateResult> => {
    const targetLineItem = lineItem || selectedLineItem;
    
    if (!targetLineItem) {
      return {
        success: false,
        message: 'No line item selected'
      };
    }

    if (targetLineItem.fulfillment_status !== 'rejected_by_admin') {
      return {
        success: false,
        message: 'Line item can only be re-accepted when it has been rejected by admin'
      };
    }

    try {
      setStatusUpdating(true);
      setStatusUpdateError(null);

      const result = await reacceptLineItemService(targetLineItem.workflow_id.toString());
      
      if (!result.success) {
        setStatusUpdateError(result.message);
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept line item';
      setStatusUpdateError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setStatusUpdating(false);
    }
  };

  const declineLineItem = async (reason?: string): Promise<StatusUpdateResult> => {
    if (!selectedLineItem) {
      return {
        success: false,
        message: 'No line item selected'
      };
    }

    if (selectedLineItem.fulfillment_status !== 'accepted_awaiting_inspection') {
      return {
        success: false,
        message: 'Line item can only be declined when it has been assigned and is awaiting inspection'
      };
    }

    try {
      setStatusUpdating(true);
      setStatusUpdateError(null);

      const result = await declineLineItemService(selectedLineItem.workflow_id.toString(), reason || 'No reason provided');
      
      if (!result.success) {
        setStatusUpdateError(result.message);
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to decline line item';
      setStatusUpdateError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setStatusUpdating(false);
    }
  };

  const acceptAllLineItems = async (): Promise<StatusUpdateResult> => {
    // Filter to only include items with 'accepted_awaiting_inspection' status
    const eligibleItems = lineItems.filter(item => item.fulfillment_status === 'accepted_awaiting_inspection');
    
    if (eligibleItems.length === 0) {
      return {
        success: false,
        message: 'No line items awaiting review to accept'
      };
    }

    try {
      setStatusUpdating(true);
      setStatusUpdateError(null);

      const results: StatusUpdateResult[] = [];

      for (const lineItem of eligibleItems) {
        const result = await acceptLineItemService(lineItem.workflow_id.toString());
        results.push(result);
      }

      const failedCount = results.filter(r => !r.success).length;
      const successCount = results.filter(r => r.success).length;

      if (failedCount === 0) {
        return {
          success: true,
          message: `Successfully accepted all ${successCount} eligible line items`
        };
      } else if (successCount === 0) {
        const errorMessage = `Failed to accept all eligible line items: ${results.find(r => !r.success)?.message}`;
        setStatusUpdateError(errorMessage);
        return {
          success: false,
          message: errorMessage
        };
      } else {
        const errorMessage = `Partially successful: ${successCount} accepted, ${failedCount} failed`;
        setStatusUpdateError(errorMessage);
        return {
          success: false,
          message: errorMessage
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to accept eligible line items';
      setStatusUpdateError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setStatusUpdating(false);
    }
  };

  const declineAllLineItems = async (reason?: string): Promise<StatusUpdateResult> => {
    // Filter to only include items with 'accepted_awaiting_inspection' status
    const eligibleItems = lineItems.filter(item => item.fulfillment_status === 'accepted_awaiting_inspection');
    
    if (eligibleItems.length === 0) {
      return {
        success: false,
        message: 'No line items awaiting review to decline'
      };
    }

    try {
      setStatusUpdating(true);
      setStatusUpdateError(null);

      const results: StatusUpdateResult[] = [];

      for (const lineItem of eligibleItems) {
        const result = await declineLineItemService(lineItem.workflow_id.toString(), reason || 'No reason provided');
        results.push(result);
      }

      const failedCount = results.filter(r => !r.success).length;
      const successCount = results.filter(r => r.success).length;

      if (failedCount === 0) {
        return {
          success: true,
          message: `Successfully declined all ${successCount} eligible line items`
        };
      } else if (successCount === 0) {
        const errorMessage = `Failed to decline all eligible line items: ${results.find(r => !r.success)?.message}`;
        setStatusUpdateError(errorMessage);
        return {
          success: false,
          message: errorMessage
        };
      } else {
        const errorMessage = `Partially successful: ${successCount} declined, ${failedCount} failed`;
        setStatusUpdateError(errorMessage);
        return {
          success: false,
          message: errorMessage
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to decline eligible line items';
      setStatusUpdateError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setStatusUpdating(false);
    }
  };



  const reset = () => {
    setSelectedLineItem(null);
    setLineItems([]);
    setLoading(false);
    setError(null);
    setUpdating(false);
    setUpdateError(null);
    setStatusUpdating(false);
    setStatusUpdateError(null);
    
    // Clear the auto-refresh interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchLineItems();
  }, [orderId]);

  // Auto-refresh setup
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Only set up auto-refresh if we have an orderId
    if (orderId) {
      intervalRef.current = setInterval(() => {
        fetchLineItemsSilently();
      }, 10000); // 10 seconds
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [orderId]); // Re-setup interval when orderId changes

  return {
    lineItems,
    loading,
    error,
    refetch: fetchLineItems,
    reset,
    selectedLineItem,
    setSelectedLineItem,
    updateLineItemsBulk: handleUpdateLineItemsBulk,
    updating,
    updateError,
    acceptLineItem,
    declineLineItem,
    acceptAllLineItems,
    declineAllLineItems,
    statusUpdating,
    statusUpdateError,
    reacceptLineItem
  };
};